'use client';

import { Button } from '@smartproxy/decoro/v2/button';
import { Card } from '@smartproxy/decoro/v2/card';
import { CheckSmallIcon } from '@smartproxy/decoro/v2/icons';
import { Text } from '@smartproxy/decoro/v2/text';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { PHTrackerProps } from '@/app/types';
import { phTrackerKey } from '@/integrations/trackingScripts/utils';

import { getBentoCtaLinkProps } from './getBentoCtaLinkProps';

export type BentoClickableCardCopyCode = {
  label: string;
  code: string;
};

export type BentoClickableCardProps = {
  title: string;
  subtitle: string;
  description?: RichTextFieldStrapiProps;
  illustration?: ImageStrapiProps;
  url?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaTarget?: '_blank';
  copyCodeButton?: BentoClickableCardCopyCode;
  className?: string;
  onClick?: () => void;
  onCtaClick?: () => void;
} & PHTrackerProps;

const COPY_FEEDBACK_MS = 3_000;

function CopyCodeButton({ label, code }: BentoClickableCardCopyCode) {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  async function handleCopy() {
    if (isCopied) return;

    await navigator.clipboard.writeText(code);
    setIsCopied(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, COPY_FEEDBACK_MS);
  }

  return (
    <Button
      type='button'
      variant='outline'
      skin='dark'
      size='small'
      width='100%'
      className='relative z-30 w-full'
      onClick={handleCopy}
      prefix={
        isCopied ? <CheckSmallIcon aria-hidden className='size-4' /> : undefined
      }
      aria-live='polite'
    >
      {isCopied ? 'Copied' : label}
    </Button>
  );
}

export const BentoClickableCard = ({
  title,
  subtitle,
  description,
  illustration,
  url,
  ctaLabel,
  ctaHref,
  ctaTarget,
  [phTrackerKey]: phTracker,
  copyCodeButton,
  className,
  onClick,
  onCtaClick,
}: BentoClickableCardProps) => {
  const hasActions = !!ctaLabel || !!copyCodeButton?.code;

  return (
    <Card
      theme='dark'
      radius='lg'
      className={cn(
        'relative border-0 bg-gray-dark-800',
        url &&
          'transition-shadow duration-200 hover:shadow-[0_0_0_1px_rgb(var(--color-brand-500))]',
        className
      )}
    >
      <div className='relative h-44 w-full shrink-0 bg-gray-dark-800'>
        {illustration && (
          <ImageStrapi
            block={illustration}
            width={379}
            height={189}
            inner={{
              objectFit: 'cover',
              classNames: {
                container: 'absolute inset-0 min-w-0 h-full w-full',
                image: 'h-full w-full object-cover',
              },
            }}
          />
        )}
        <div className='absolute bottom-2.5 left-3 z-10 flex w-44 flex-col gap-1'>
          <Text
            variant='label_l'
            weight='medium'
            skin='white'
            className='whitespace-nowrap text-gray-dark-200'
          >
            {title}
          </Text>
          <Text variant='body_s' className='text-gray-dark-400'>
            {subtitle}
          </Text>
        </div>
      </div>

      {(description || hasActions) && (
        <div className='flex w-full flex-col gap-3 p-3'>
          {description && (
            <RichTextFieldStrapi
              block={description}
              classNames={{
                paragraph: 'text-body-s leading-4 text-gray-dark-400 font-sans',
                listUnordered:
                  'ml-0 list-disc gap-0 pl-5 text-body-s leading-4 text-gray-dark-400 font-sans',
                listOrdered:
                  'ml-0 list-decimal gap-0 pl-5 text-body-s leading-4 text-gray-dark-400 font-sans',
              }}
            />
          )}

          {hasActions && (
            <div className='relative z-30 flex w-full flex-col gap-2'>
              {ctaLabel && (
                <Button
                  {...getBentoCtaLinkProps(ctaHref, ctaTarget, phTracker)}
                  variant='outline'
                  skin='dark'
                  size='small'
                  width='100%'
                  className='w-full'
                  onClick={onCtaClick}
                >
                  {ctaLabel}
                </Button>
              )}
              {copyCodeButton?.code && (
                <CopyCodeButton
                  label={copyCodeButton.label}
                  code={copyCodeButton.code}
                />
              )}
            </div>
          )}
        </div>
      )}

      {url && (
        <a
          href={url}
          aria-label={title}
          onClick={onClick}
          className='absolute inset-0 z-20 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white'
        />
      )}
    </Card>
  );
};
